/**
 * The legal documents a user must accept, and which version of each counts.
 * Below them, the documents a user only reads, which carry no acceptance at all.
 *
 * Two numbers per policy, and the gap between them is the whole mechanism:
 * `currentVersion` moves on every edit so the record stays exact, while
 * `minimumAcceptedVersion` moves only when Legal calls a change material. A
 * stored acceptance below the floor is stale, so raising the floor re-prompts
 * everyone who accepted under the old wording and raising only the version
 * ships a correction silently.
 *
 * `packages/api/src/policies.py` holds the same two numbers and is what actually
 * enforces them; this copy decides what the user is shown. `README.md` in this
 * directory is the procedure for changing either.
 */

import {
  parseInformationDocument,
  parsePolicyDocument,
  type InformationDocument,
  type PolicyDocument,
} from "./parse";
import aiSystemCardV1 from "./ai-system-card/v1.md?raw";
import aiTransparencyPrivacyNoticeV2 from "./ai-transparency-privacy-notice/v2.md?raw";
import privacyAndCookieNoticeV1 from "./privacy-and-cookie-notice/v1.md?raw";
import termsOfUseV2 from "./terms-of-use/v2.md?raw";

/** In the order the legal surfaces list them. */
export const POLICY_IDS = [
  "terms-of-use",
  "ai-transparency-privacy-notice",
] as const;

export type PolicyId = (typeof POLICY_IDS)[number];

interface PolicyRegistration {
  currentVersion: number;
  minimumAcceptedVersion: number;
  /**
   * Only the current version is bundled. Superseded versions stay in the repo
   * as the record of what a stored acceptance refers to, but shipping every one
   * of them would grow the bundle for every edit ever made.
   */
  raw: string;
}

const REGISTRY: Record<PolicyId, PolicyRegistration> = {
  "terms-of-use": {
    currentVersion: 2,
    minimumAcceptedVersion: 2,
    raw: termsOfUseV2,
  },
  "ai-transparency-privacy-notice": {
    currentVersion: 2,
    minimumAcceptedVersion: 2,
    raw: aiTransparencyPrivacyNoticeV2,
  },
};

export interface Policy {
  id: PolicyId;
  currentVersion: number;
  minimumAcceptedVersion: number;
  document: PolicyDocument;
}

/**
 * Parsed at module load, so a malformed document fails the import rather than
 * rendering a consent dialog with a blank tick box label.
 */
export const POLICIES: readonly Policy[] = POLICY_IDS.map((id) => {
  const registration = REGISTRY[id];
  return {
    id,
    currentVersion: registration.currentVersion,
    minimumAcceptedVersion: registration.minimumAcceptedVersion,
    document: parsePolicyDocument(registration.raw),
  };
});

export function findPolicy(id: string): Policy | undefined {
  return POLICIES.find((policy) => policy.id === id);
}

/**
 * Legal documents a user can read but is never asked to accept.
 *
 * They are versioned the same way, so a link to one names the words it held,
 * but there is no floor: nothing is stored when someone reads one, so there is
 * no acceptance for a new version to make stale. That is also why they are
 * absent from `packages/api/src/policies.py` and the consent dialog.
 */
export const INFORMATION_DOCUMENT_IDS = [
  "privacy-and-cookie-notice",
  "ai-system-card",
] as const;

export type InformationDocumentId = (typeof INFORMATION_DOCUMENT_IDS)[number];

const INFORMATION_REGISTRY: Record<
  InformationDocumentId,
  { currentVersion: number; raw: string }
> = {
  "privacy-and-cookie-notice": {
    currentVersion: 1,
    raw: privacyAndCookieNoticeV1,
  },
  "ai-system-card": {
    currentVersion: 1,
    raw: aiSystemCardV1,
  },
};

export interface InformationEntry {
  id: InformationDocumentId;
  currentVersion: number;
  document: InformationDocument;
}

export const INFORMATION_DOCUMENTS: readonly InformationEntry[] =
  INFORMATION_DOCUMENT_IDS.map((id) => ({
    id,
    currentVersion: INFORMATION_REGISTRY[id].currentVersion,
    document: parseInformationDocument(INFORMATION_REGISTRY[id].raw),
  }));

/** Either kind of document, as the legal surfaces that only show it see it. */
export interface LegalDocument {
  id: PolicyId | InformationDocumentId;
  currentVersion: number;
  document: InformationDocument;
}

/**
 * Every legal document, accepted ones first, for the pages that list and show
 * them. The consent gate reads `POLICIES` instead, so nothing here is ever a
 * tick box.
 */
export const LEGAL_DOCUMENTS: readonly LegalDocument[] = [
  ...POLICIES,
  ...INFORMATION_DOCUMENTS,
];

export function findLegalDocument(id: string): LegalDocument | undefined {
  return LEGAL_DOCUMENTS.find((entry) => entry.id === id);
}
